import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/features/auth/api';
import { brandProfileSchema, COMPANY_KINDS, type BrandProfileInput } from '@/lib/schemas/brandKit';
import { useAuthStore } from '@/lib/auth/authStore';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import { OnboardingShell } from '@/components/layout/OnboardingShell';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import type { TranslationKey } from '@/lib/i18n/translations';
import { cn } from '@/lib/cn';

export function BrandProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const patchUser = useAuthStore((s) => s.patchUser);
  const t = useT();
  const { company } = useCurrentCompany();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<BrandProfileInput>({ resolver: zodResolver(brandProfileSchema) });
  // Para pintar cuál está elegida: el radio va oculto (`sr-only`) porque la
  // tarjeta entera es el control, así que el estado seleccionado tiene que
  // salir del formulario y no del `:checked` del navegador.
  const elegido = watch('companyType');

  // Pre-fill the company name from the company created during registration so
  // the user doesn't retype it. keepDirtyValues preserves anything already
  // edited if the company query resolves after the form rendered.
  useEffect(() => {
    if (company?.name) {
      reset({ companyName: company.name }, { keepDirtyValues: true });
    }
  }, [company?.name, reset]);

  const save = useMutation({
    mutationFn: (input: BrandProfileInput) => authApi.completeProfile(input),
    onSuccess: ({ user }) => {
      patchUser({ ...user });
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.current });
      toast({
        tone: 'success',
        title: t('onboarding.profile.savedTitle'),
        description: t('onboarding.profile.savedBody'),
      });
      navigate('/onboarding/brandkit', { replace: true });
    },
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('onboarding.profile.errorTitle'),
        description: (e as Error).message,
      }),
  });

  return (
    <OnboardingShell
      step={2}
      title={t('onboarding.profile.title')}
      subtitle={t('onboarding.profile.subtitle')}
    >
      <form noValidate className="grid gap-5" onSubmit={handleSubmit((v) => save.mutate(v))}>
        <FormField
          label={t('onboarding.profile.companyName')}
          required
          error={errors.companyName?.message}
        >
          <Input
            placeholder={t('onboarding.profile.companyNamePlaceholder')}
            {...register('companyName')}
          />
        </FormField>
        <FormField
          label={t('onboarding.profile.yourName')}
          required
          error={errors.contactName?.message}
        >
          <Input
            placeholder={t('onboarding.profile.yourNamePlaceholder')}
            {...register('contactName')}
          />
        </FormField>
        {/*
          Qué es esta cuenta. Sustituye a «industria», que no decidía nada.
          Tres tarjetas y no un desplegable: son tres identidades, no una lista
          de opciones — se leen de un vistazo y se responden con un dedo, que es
          lo que hace falta en el segundo minuto de alguien que acaba de
          registrarse.
        */}
        <FormField
          label={t('onboarding.profile.kind')}
          required
          error={errors.companyType?.message}
        >
          <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
            {COMPANY_KINDS.map((kind) => {
              const activo = elegido === kind;
              return (
                <label
                  key={kind}
                  className={cn(
                    'cursor-pointer rounded-xl border p-3 transition-colors',
                    activo
                      ? 'border-[var(--color-xk-accent)] bg-[var(--color-xk-surface)]'
                      : 'border-[var(--color-xk-border)] hover:border-[var(--color-accent-ink)]',
                  )}
                >
                  <input
                    type="radio"
                    value={kind}
                    className="sr-only"
                    {...register('companyType')}
                  />
                  <div className="text-sm font-bold text-[var(--color-xk-lavender)]">
                    {t(`onboarding.profile.kind.${kind}` as TranslationKey)}
                  </div>
                  <div className="mt-1 text-[11px] leading-snug text-[var(--color-xk-text-muted)]">
                    {t(`onboarding.profile.kind.${kind}.hint` as TranslationKey)}
                  </div>
                </label>
              );
            })}
          </div>
        </FormField>
        <div className="flex justify-end pt-2">
          <Button type="submit" loading={save.isPending} size="lg">
            {t('onboarding.profile.continue')}
          </Button>
        </div>
      </form>
    </OnboardingShell>
  );
}
